'use client';

import {
  AriaRole,
  useCallback,
  useRef,
  type PropsWithChildren,
  type FC
} from 'react';
import type { Timeout } from '@/type-utils';
import { mediaMatches } from '../../core-ui/Breakpoints';

import S from './styles.module.scss';

export interface IHoverIntentProps extends PropsWithChildren {
  /**
   * Callback that will be called when the hover intent is activated or deactivated.
   * @param active - The current state of the hover intent.
   */
  onHoverChange?: (active: boolean) => void;

  /** Callback that will be called when the hover intent is activated. */
  onHoverIn?: () => void;

  /** Callback that will be called when the hover intent is deactivated. */
  onHoverOut?: () => void;

  /** Delay in milliseconds to trigger the hover action. Defaults to `175`. */
  delay?: number;

  /**
   * Delay in milliseconds to deactivate the hover intent after the mouse leaves.
   * If unspecified, it will use the same value as `delay`.
   */
  delayOut?: number;

  /**
   * The [ARIA Role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles) to use.
   * It is only advised to use interactive ARIA roles on hoverable elements.
   */
  ariaRole?: AriaRole;

  /**
   * Tab index. Please keep in mind that due to a11y guidelines, tabbable elements must also
   * include an [interactive ARIA role](https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/main/docs/rules/no-noninteractive-tabindex.md).
   */
  tabIndex?: number;

  /** If the hover intent should be activated on focus. Defaults to `true`. */
  activatedByFocus?: boolean;

  /** If the hover intent should be deactivated on blur. Defaults to `true`. */
  deactivatedByBlur?: boolean;

  /**
   * If the hover intent should be disabled. Use this prop to conditionally
   * allow hovering.
   */
  disabled?: boolean;
}

/**
 * Utility component that notifies (via the supplied callbacks) when the user
 * shows the intention of hovering over its contents.
 *
 * @example
 *
 * ```tsx
 * // TASK: Make it so a tooltip appears when the user hovers over the Save button.
 *
 * const [active, setActive] = useState<boolean>(false);
 *
 * return (
 *      <HoverIntent onHoverChange={setPanelActive}>
 *          <Tooltip visible={active} body="Saves the latest changes"/>
 *          <Button>Save</Button>
 *      </HoverIntent>
 * );
 *
 * // Or, if you want discrete callbacks for in and out:
 * return (
 *      <HoverIntent
 *        onHoverIn={() => setPanelActive(true)}
 *        onHoverOut={() => setPanelActive(false)}
 *      >
 *          <Tooltip visible={active} body="Saves the latest changes"/>
 *          <Button>Save</Button>
 *      </HoverIntent>
 * );
 * ```
 */
export const HoverIntent: FC<IHoverIntentProps> = ({
  onHoverChange,
  onHoverIn,
  onHoverOut,
  delay = 175,
  delayOut = delay,
  ariaRole = 'link',
  tabIndex = 0,
  activatedByFocus = true,
  deactivatedByBlur = true,
  disabled = false,
  children
}) => {
  const ref = useRef<Timeout>(undefined);

  // Activate will...
  const activate = useCallback(() => {
    // Do nothing if the hover intent is disabled or if we're currently at a mobile
    // breakpoint.
    if (disabled || !mediaMatches(window, 'desktop')) return;

    if (onHoverChange) onHoverChange(true); // Call both `onHoverChange` with `true`...
    if (onHoverIn) onHoverIn(); // ...and `onHoverIn`.
  }, [disabled, onHoverChange, onHoverIn]);

  // Deactivate will...
  const deactivate = useCallback(() => {
    // Do nothing if the hover intent is disabled or if we're currently at a mobile
    // breakpoint.
    if (disabled || !mediaMatches(window, 'desktop')) return;

    if (onHoverChange) onHoverChange(false); // Call both `onHoverChange` with `false`...
    if (onHoverOut) onHoverOut(); // ...and `onHoverOut`.
  }, [disabled, onHoverChange, onHoverOut]);

  const timeoutActivate = useCallback(() => {
    // Clear the current timeout (if any)...
    if (ref.current) clearTimeout(ref.current);

    // ...and set a new timeout to call `activate`.
    ref.current = setTimeout(() => {
      activate();
    }, delay);
  }, [ref, activate, delay]);

  const timeoutDeactivate = useCallback(() => {
    // Clear the current timeout (if any)...
    if (ref.current) clearTimeout(ref.current);

    // ...and set a new timeout to call `deactivate`.
    ref.current = setTimeout(() => {
      deactivate();
    }, delayOut);
  }, [ref, deactivate, delayOut]);

  return (
    <div
      className={S.hoverIntent}
      role={ariaRole}
      tabIndex={tabIndex}
      onMouseEnter={timeoutActivate}
      onMouseLeave={timeoutDeactivate}
      onFocus={activatedByFocus ? activate : undefined}
      onBlur={deactivatedByBlur ? deactivate : undefined}
    >
      {children}
    </div>
  );
};
